/**
 * The reaction table — docs/03-materials.md §5.1 (tag-level) and §5.2
 * (signature concrete pairs), in that order.
 *
 * Authoring notes:
 *  - `tagRule` is the point of the whole system: one row covers every material
 *    that declares the tag, including ones that do not exist yet.
 *  - `pairRule` is for interactions with personality. It wins over any tag rule
 *    covering the same two materials, which is how the two-stage acid-on-iron
 *    chain pre-empts the broad CORROSIVE × METAL row.
 *  - `mixRule` / `matRule` are the half-and-half cases: a tag on one side and a
 *    named material on the other. They sit between the two in priority.
 *  - Per-material thresholds the schema stores as properties (`freezesAt`,
 *    `ignitionTemp`) are written as explicit `maxTemp`/`minTemp` here, except
 *    `ignitionTemp`, which reactions.ts enforces for every BURNING rule.
 *
 * Rows the engine deliberately does not own:
 *  - `CONDUCTIVE`+charged × `WET` and `MERCURY` + charge — charge.ts
 *  - `FROZEN` × impact ≥ 40 — there is no impact at a cell contact; the combat
 *    layer routes it through `world.damageCell`, which already reads
 *    `brittleness`/`shatteredInto`
 *  - `TEMPORAL` × projectile and `CHRONO_FLUID` + thrown weapon — entity layer
 *  - `SEALED` volume × pressure > rupture — pressure.ts
 *  - combustion itself (fuel burn, ash conversion) — thermal.ts; rules here
 *    only set `BURNING`
 */

import { MAT, type MaterialName } from './materialData';
import { Flag, Tag, type TagMask } from './materials';
import type { ReactionRule } from './reactions';

type OutSpec = MaterialName | 'keep' | 'consume';

interface RuleOpts {
  chance?: number;
  minTemp?: number;
  maxTemp?: number;
  requiresFlagA?: number;
  requiresFlagB?: number;
  forbidsFlagA?: number;
  forbidsFlagB?: number;
  outA?: OutSpec;
  outB?: OutSpec;
  heat?: number;
  flagA?: number;
  flagB?: number;
  emit?: { mat: MaterialName; count: number; velocity?: number };
  charge?: number;
  event?: string;
  priority?: number;
}

function id(name: MaterialName): number {
  const v = MAT[name];
  if (v === undefined) {
    throw new Error(`reaction rule references "${name}" before registerMaterials()`);
  }
  return v;
}

function out(v: OutSpec | undefined): number | 'keep' | 'consume' | undefined {
  if (v === undefined || v === 'keep' || v === 'consume') return v;
  return id(v);
}

function build(
  a: number,
  aIsTag: boolean,
  b: number,
  bIsTag: boolean,
  o: RuleOpts,
  defaultPriority: number,
): ReactionRule {
  return {
    a,
    b,
    aIsTag,
    bIsTag,
    chance: o.chance ?? 0.5,
    minTemp: o.minTemp,
    maxTemp: o.maxTemp,
    requiresFlagA: o.requiresFlagA,
    requiresFlagB: o.requiresFlagB,
    forbidsFlagA: o.forbidsFlagA,
    forbidsFlagB: o.forbidsFlagB,
    outA: out(o.outA),
    outB: out(o.outB),
    heat: o.heat,
    flagA: o.flagA,
    flagB: o.flagB,
    emit: o.emit === undefined
      ? undefined
      : { mat: id(o.emit.mat), count: o.emit.count, velocity: o.emit.velocity },
    charge: o.charge,
    event: o.event,
    priority: o.priority ?? defaultPriority,
  };
}

/** Tag × tag. The broad rows. */
const tagRule = (aTags: TagMask, bTags: TagMask, o: RuleOpts): ReactionRule =>
  build(aTags, true, bTags, true, o, 0);

/** Tag × named material. */
const mixRule = (aTags: TagMask, bName: MaterialName, o: RuleOpts): ReactionRule =>
  build(aTags, true, id(bName), false, o, 5);

/** Named material × tag. */
const matRule = (aName: MaterialName, bTags: TagMask, o: RuleOpts): ReactionRule =>
  build(id(aName), false, bTags, true, o, 5);

/** Named × named. The signature interactions. */
const pairRule = (aName: MaterialName, bName: MaterialName, o: RuleOpts): ReactionRule =>
  build(id(aName), false, id(bName), false, o, 10);

/**
 * Anything that can plausibly be on fire. Used as the B side of rules gated on
 * `requiresFlagB: BURNING`, where the flag is the real condition and the mask
 * only exists to keep the candidate index small.
 */
const COMBUSTIBLE =
  Tag.FLAMMABLE | Tag.ACCELERANT | Tag.ORGANIC | Tag.BIOMASS | Tag.METAL | Tag.MACHINE;

/** Liquids that behave as water for extinguishing and soaking. */
const WATERLIKE: MaterialName[] = ['WATER', 'SALTWATER', 'BRINE'];

/** Open flame — the PYRO materials a splash of water actually puts out. */
const OPEN_FLAME: MaterialName[] = ['FIRE', 'EMBER_GRIT'];

export function buildReactionRules(): ReactionRule[] {
  const R: ReactionRule[] = [];

  // ==================================================== §5.1 tag-level rules

  // PYRO × FLAMMABLE — universal ignition. The temp ≥ ignitionTemp condition is
  // per-material, so reactions.ts enforces it for every rule that sets BURNING.
  R.push(
    tagRule(Tag.PYRO, Tag.FLAMMABLE, {
      chance: 0.4,
      flagB: Flag.BURNING,
      forbidsFlagB: Flag.WET,
      event: 'ignition',
    }),
  );

  // BURNING × ACCELERANT — +80 °C and double the spread chance.
  R.push(
    tagRule(Tag.PYRO | Tag.FLAMMABLE, Tag.ACCELERANT, {
      chance: 0.8,
      requiresFlagA: Flag.BURNING,
      forbidsFlagB: Flag.WET,
      heat: 80,
      flagB: Flag.BURNING,
      event: 'accelerant_flashover',
      priority: 1,
    }),
  );

  // BURNING × ORGANIC — the ash conversion is thermal.ts's fuel timer; here it
  // is ignition plus the smoke that fills the room.
  R.push(
    tagRule(Tag.PYRO | Tag.FLAMMABLE, Tag.ORGANIC, {
      chance: 0.3,
      requiresFlagA: Flag.BURNING,
      forbidsFlagB: Flag.WET,
      flagB: Flag.BURNING,
      emit: { mat: 'SMOKE', count: 1 },
      event: 'organic_combustion',
    }),
  );

  // BURNING × METAL — metals heat, they do not burn.
  R.push(
    tagRule(Tag.PYRO | Tag.FLAMMABLE, Tag.METAL, {
      chance: 0.6,
      requiresFlagA: Flag.BURNING,
      heat: 40,
      event: 'metal_heats',
    }),
  );

  // BURNING × MACHINE above 300 °C — overheat, venting smoke.
  R.push(
    tagRule(Tag.PYRO | Tag.FLAMMABLE, Tag.MACHINE, {
      chance: 0.4,
      minTemp: 300,
      requiresFlagA: Flag.BURNING,
      heat: 25,
      emit: { mat: 'SMOKE', count: 1 },
      event: 'machine_overheat',
    }),
  );

  // WATER-like × PYRO — A → STEAM, B extinguished. Lava, molten iron and
  // coolant have their own signature rules and are not covered here.
  for (const w of WATERLIKE) {
    for (const f of OPEN_FLAME) {
      R.push(
        pairRule(w, f, {
          chance: 0.9,
          outA: 'STEAM',
          outB: 'consume',
          heat: -60,
          event: 'water_extinguish',
        }),
      );
    }
    // The other half of extinguishing: a burning solid goes WET, and the engine
    // treats WET as the end of BURNING.
    R.push(
      matRule(w, COMBUSTIBLE, {
        chance: 0.85,
        requiresFlagB: Flag.BURNING,
        outA: 'STEAM',
        flagB: Flag.WET,
        heat: -40,
        event: 'water_quench',
      }),
    );
  }

  // CHARGED × MACHINE — overload, 30% short.
  R.push(
    tagRule(Tag.CONDUCTIVE, Tag.MACHINE, {
      chance: 0.3,
      requiresFlagA: Flag.CHARGED,
      heat: 60,
      emit: { mat: 'SMOKE', count: 1 },
      event: 'machine_short',
    }),
  );
  // A circuit plate that shorts is destroyed outright — and throws an arc.
  // The arc is why LIGHTNING exists as a material rather than as a weapon-only
  // effect: it is the first link of the docs/03 §5.2 chain
  //   short -> LIGHTNING -> CRYSTAL stores it -> released as a PLASMA beam
  //   -> plasma meets REACTOR_COOLANT -> FLASH_VAPOR -> pressure -> rupture
  // and without an in-world producer that whole chain was unreachable terrain.
  R.push(
    mixRule(Tag.CONDUCTIVE, 'CIRCUIT_PLATE', {
      chance: 0.15,
      requiresFlagA: Flag.CHARGED,
      outB: 'NANITE_DUST',
      heat: 90,
      emit: { mat: 'LIGHTNING', count: 1, velocity: 1 },
      event: 'circuit_burnout',
    }),
  );

  // CHARGED × ORGANIC — damage and STUN land on entities; the cell keeps the
  // scorch and the codex entry.
  R.push(
    tagRule(Tag.CONDUCTIVE, Tag.ORGANIC, {
      chance: 0.25,
      requiresFlagA: Flag.CHARGED,
      heat: 25,
      event: 'charge_shock',
    }),
  );

  // CORROSIVE × ORGANIC — fast. Chance is scaled by the attacker's
  // corrosiveness and the defender's corrosionResist inside the engine.
  R.push(
    tagRule(Tag.CORROSIVE, Tag.ORGANIC, {
      chance: 0.55,
      outB: 'consume',
      emit: { mat: 'TOXIC_SMOKE', count: 1 },
      event: 'acid_dissolves_organics',
    }),
  );

  // CORROSIVE × METAL — slow, two-stage. Iron rusts, rust dissolves.
  R.push(
    mixRule(Tag.CORROSIVE, 'IRON', {
      chance: 0.3,
      outB: 'RUSTED_IRON',
      event: 'acid_rusts_iron',
    }),
    mixRule(Tag.CORROSIVE, 'STEEL', {
      chance: 0.2,
      outB: 'RUSTED_IRON',
      event: 'acid_rusts_iron',
    }),
    mixRule(Tag.CORROSIVE, 'RUSTED_IRON', {
      chance: 0.35,
      outB: 'consume',
      emit: { mat: 'TOXIC_SMOKE', count: 1 },
      event: 'acid_consumes_rust',
    }),
    mixRule(Tag.CORROSIVE, 'RUST_FLAKE', { chance: 0.5, outB: 'consume' }),
    // Everything else metallic corrodes straight to flakes.
    tagRule(Tag.CORROSIVE, Tag.METAL, {
      chance: 0.12,
      outB: 'RUST_FLAKE',
      event: 'acid_corrodes_metal',
    }),
  );

  // CORROSIVE × STONE, hardness ≤ 4 — soft stone only, so it is written against
  // the three materials that qualify rather than against a hardness test.
  R.push(
    mixRule(Tag.CORROSIVE, 'LIMESTONE', {
      chance: 0.25,
      outB: 'consume',
      emit: { mat: 'TOXIC_SMOKE', count: 1 },
      event: 'acid_eats_stone',
    }),
    mixRule(Tag.CORROSIVE, 'SANDSTONE', { chance: 0.25, outB: 'SAND', event: 'acid_eats_stone' }),
    mixRule(Tag.CORROSIVE, 'GRAVEL', { chance: 0.2, outB: 'consume' }),
  );

  // CORROSIVE × CERAMIC — no reaction, and deliberately no rule. Ceramic's
  // corrosionResist of 1.0 zeroes the chance of every corrosive rule above, so
  // the counter cannot be broken by adding a broader tag row later.

  // CORROSIVE × SACRED — the ward neutralises the acid.
  R.push(
    tagRule(Tag.CORROSIVE, Tag.SACRED, {
      chance: 0.5,
      outA: 'consume',
      event: 'ward_neutralises_acid',
      priority: 1,
    }),
  );

  // CRYO × LIQUID at or below freezesAt. The threshold is per-material, so each
  // freezable liquid states its own.
  R.push(
    mixRule(Tag.CRYO, 'WATER', { chance: 0.3, maxTemp: 0, outB: 'ICE', event: 'cryo_freeze' }),
    mixRule(Tag.CRYO, 'SALTWATER', { chance: 0.3, maxTemp: -2, outB: 'ICE' }),
    mixRule(Tag.CRYO, 'BRINE', { chance: 0.3, maxTemp: -10, outB: 'ICE' }),
    mixRule(Tag.CRYO, 'BLOOD', { chance: 0.3, maxTemp: -2, outB: 'ICE' }),
    mixRule(Tag.CRYO, 'MUD', { chance: 0.25, maxTemp: -4, outB: 'PERMAFROST' }),
    mixRule(Tag.CRYO, 'CRUDE_TAR', { chance: 0.4, maxTemp: 20, outB: 'TAR' }),
    mixRule(Tag.CRYO, 'MERCURY', { chance: 0.3, maxTemp: -39, outB: 'IRON' }),
  );

  // CRYO × ORGANIC — FROZEN sets up the shatter. The brittleness swing is read
  // by world.damageCell, which is also where the impact half of the combo lives.
  R.push(
    tagRule(Tag.CRYO, Tag.ORGANIC, {
      chance: 0.3,
      maxTemp: 0,
      flagB: Flag.FROZEN,
      event: 'flash_freeze',
    }),
  );

  // FUNGAL × BIOMASS in damp conditions — ecology conversion. "Dark" is a light
  // field query the simulation layer cannot make, so damp carries the gate.
  R.push(
    tagRule(Tag.FUNGAL, Tag.BIOMASS, {
      chance: 0.06,
      requiresFlagB: Flag.WET,
      outB: 'FUNGAL_MAT',
      event: 'fungal_bloom',
    }),
  );

  // FUNGAL × PYRO — burning fungus spores harder.
  R.push(
    tagRule(Tag.FUNGAL, Tag.PYRO, {
      chance: 0.45,
      outA: 'ASH',
      emit: { mat: 'SPORE_CLOUD', count: 3, velocity: 1 },
      event: 'burning_fungus',
    }),
  );

  // FUNGAL × RADIANT — the clean counter. Outranks the PYRO row so holy fire
  // never produces a spore burst.
  R.push(
    tagRule(Tag.FUNGAL, Tag.RADIANT, {
      chance: 0.5,
      outA: 'ASH',
      event: 'radiant_purges_fungus',
      priority: 2,
    }),
  );

  // Nanites recognise their own and leave them alone. Without this, dust sitting
  // next to dust satisfies NANITE × MACHINE and a dormant pile converts itself.
  R.push(tagRule(Tag.NANITE, Tag.NANITE, { chance: 1, priority: 1 }));

  // NANITE × MACHINE — repaired or converted; faction decides, and until there
  // is a faction the swarm wins.
  R.push(
    tagRule(Tag.NANITE, Tag.MACHINE, {
      chance: 0.08,
      outB: 'NANITE_SWARM',
      event: 'nanites_convert_machine',
    }),
  );

  // NANITE × BIOMASS — the grey-goo threat.
  R.push(
    tagRule(Tag.NANITE, Tag.BIOMASS, {
      chance: 0.1,
      outB: 'NANITE_SWARM',
      event: 'grey_goo',
    }),
  );

  // NANITE × CHARGED — the EMP counter. STATIC is the inert state; the movement
  // and bioActivity systems both skip it.
  R.push(
    tagRule(Tag.NANITE, Tag.CONDUCTIVE, {
      chance: 0.6,
      requiresFlagB: Flag.CHARGED,
      flagA: Flag.STATIC,
      event: 'emp_disables_nanites',
      priority: 2,
    }),
  );

  // VOID × any non-VOID — erasure, sometimes leaving void matter behind. The
  // "non-VOID" half is an engine invariant; ANY-of tag masks cannot negate.
  R.push(
    tagRule(Tag.VOID, ~Tag.VOID, {
      chance: 0.05,
      outB: 'consume',
      event: 'void_erasure',
    }),
    tagRule(Tag.VOID, ~Tag.VOID, {
      chance: 0.02,
      outB: 'VOID_MATTER',
      event: 'void_residue',
      priority: 1,
    }),
    // Voidglass is worked, stabilised void: it drinks light (§5.2) but must not
    // inherit the erasure row, or every wall built from it would eat the room
    // around it. An inert rule above the VOID rows is the whole opt-out.
    matRule('VOIDGLASS', ~Tag.VOID, { chance: 1, priority: 4 }),
  );

  // VOID × PYRO — the flame does not go out, it goes cold. This is the only
  // producer of VOID_FLAME, which otherwise had zero references anywhere in the
  // repo outside its own definition, and it is exactly what its description
  // claims it is: "burns cold, freezes and erases in the same motion".
  // Priority 2 puts it above the erasure rows and below VOID × RADIANT, so
  // holy fire still annihilates rather than being converted.
  R.push(
    tagRule(Tag.VOID, Tag.PYRO, {
      chance: 0.45,
      outB: 'VOID_FLAME',
      heat: -120,
      event: 'void_quenches_flame',
      priority: 2,
    }),
  );

  // VOID × RADIANT — mutual annihilation with a 60 °C flash.
  R.push(
    tagRule(Tag.VOID, Tag.RADIANT, {
      chance: 0.9,
      outA: 'consume',
      outB: 'consume',
      heat: 60,
      event: 'void_radiant_annihilation',
      priority: 3,
    }),
  );

  // VOID × TEMPORAL — an unstable rift. Displacement is the residue's job once
  // something tries to cross it.
  R.push(
    tagRule(Tag.VOID, Tag.TEMPORAL, {
      chance: 0.4,
      outB: 'TEMPORAL_RESIDUE',
      emit: { mat: 'VOID_MIST', count: 2, velocity: 1 },
      event: 'void_temporal_rift',
      priority: 3,
    }),
  );

  // RADIANT × UNDEAD — burns, ignoring resistances. The heat is what carries it
  // past bone's very high ignition temperature.
  R.push(
    tagRule(Tag.RADIANT, Tag.UNDEAD, {
      chance: 0.6,
      heat: 220,
      flagB: Flag.BURNING,
      event: 'radiant_burns_undead',
      priority: 1,
    }),
  );
  // Undead matter with no ignition temperature at all is simply consumed.
  R.push(
    tagRule(Tag.RADIANT, Tag.UNDEAD, {
      chance: 0.08,
      outB: 'BONE_ASH',
      event: 'radiant_unmakes_undead',
    }),
  );

  // RADIANT × CURSED — mutual consumption.
  R.push(
    tagRule(Tag.RADIANT, Tag.CURSED, {
      chance: 0.35,
      outA: 'consume',
      outB: 'consume',
      heat: 20,
      event: 'radiance_cleanses_curse',
      priority: 2,
    }),
  );

  // RADIANT × CRYSTAL — beam focus. LIT is what the light field and the beam
  // weapons read for the +60% range.
  R.push(
    tagRule(Tag.RADIANT, Tag.CRYSTAL, {
      chance: 0.6,
      flagB: Flag.LIT,
      event: 'crystal_focuses_beam',
      priority: 2,
    }),
  );

  // BLOOD × CURSED — blood turns, and the curse is fed.
  R.push(
    tagRule(Tag.BLOOD, Tag.CURSED, {
      chance: 0.3,
      outA: 'CURSED_BLOOD',
      flagB: Flag.CURSED,
      event: 'blood_curdles',
      priority: 1,
    }),
  );

  // BLOOD × FUNGAL — accelerated bloom, ×3 the plain conversion rate.
  R.push(
    tagRule(Tag.BLOOD, Tag.FUNGAL, {
      chance: 0.18,
      outA: 'FUNGAL_MAT',
      event: 'blood_bloom',
      priority: 1,
    }),
  );

  // BLOOD × SACRED — purified, and the ward brightens.
  R.push(
    tagRule(Tag.BLOOD, Tag.SACRED, {
      chance: 0.3,
      outA: 'WATER',
      flagB: Flag.SANCTIFIED,
      event: 'blood_purified',
      priority: 2,
    }),
  );

  // TOXIC × ORGANIC — damage per tick scaled by toxicity, which the status
  // system reads off the INFECTED flag.
  R.push(
    tagRule(Tag.TOXIC, Tag.ORGANIC, {
      chance: 0.12,
      flagB: Flag.INFECTED,
      event: 'toxic_contact',
    }),
  );

  // SOLUBLE × LIQUID — A dissolves and B inherits. Salt is the one that changes
  // what the liquid *is*; everything else simply goes away.
  R.push(
    pairRule('SALT', 'WATER', {
      chance: 0.35,
      outA: 'consume',
      outB: 'SALTWATER',
      event: 'salt_dissolves',
    }),
    pairRule('SALT', 'SALTWATER', {
      chance: 0.25,
      outA: 'consume',
      outB: 'BRINE',
      event: 'brine_saturates',
    }),
  );
  // Water, saltwater, brine and alcohol are SOLUBLE themselves — mixing them is
  // a no-op, and a tag-level SOLUBLE × liquid row would have water dissolving
  // water out of existence. The row is written against the soluble *solids*.
  for (const w of WATERLIKE) {
    R.push(
      pairRule('ALCHEMICAL_DUST', w, { chance: 0.25, outA: 'consume', event: 'dissolves' }),
    );
  }

  // POROUS × LIQUID — A gains WET, B is consumed slowly. This is also what
  // makes damp ground the substrate fungus needs.
  for (const w of WATERLIKE) {
    R.push(
      matRule(w, Tag.POROUS, {
        chance: 0.08,
        outA: 'consume',
        flagB: Flag.WET,
        event: 'porous_soaks',
      }),
    );
  }

  // EXPLOSIVE above 200 °C — detonation. The impact trigger is combat's; the
  // heat trigger is a contact with anything PYRO.
  R.push(
    tagRule(Tag.EXPLOSIVE, Tag.PYRO, {
      chance: 0.9,
      minTemp: 200,
      outA: 'consume',
      heat: 350,
      emit: { mat: 'FIRE', count: 3, velocity: 2 },
      event: 'detonation',
      priority: 2,
    }),
  );

  // ================================================ §5.2 signature concrete pairs

  // WATER + LAVA — obsidian and a violent steam burst. Terrain creation, and
  // the single most important pair in the game.
  for (const w of WATERLIKE) {
    R.push(
      pairRule(w, 'LAVA', {
        chance: 0.95,
        outA: 'STEAM',
        outB: 'OBSIDIAN',
        heat: -300,
        emit: { mat: 'STEAM', count: 3, velocity: 2 },
        event: 'water_lava_obsidian',
      }),
    );
    // The same trick on a molten enemy: flash-solidified back to iron.
    R.push(
      pairRule(w, 'MOLTEN_IRON', {
        chance: 0.8,
        outA: 'STEAM',
        outB: 'IRON',
        heat: -260,
        emit: { mat: 'STEAM', count: 2, velocity: 1 },
        event: 'molten_iron_quenched',
      }),
    );
  }

  // WATER + ACID — dilution. The acid weakens, the water comes away mildly
  // corrosive.
  R.push(
    pairRule('WATER', 'ACID', {
      chance: 0.12,
      outA: 'BRINE',
      outB: 'consume',
      event: 'acid_diluted',
    }),
  );

  // OIL + WATER — density already floats the oil; this row exists so the codex
  // records the burnable surface layer the player just built.
  R.push(pairRule('OIL', 'WATER', { chance: 0.05, event: 'oil_slick_on_water' }));

  // OIL + FIRE — ×2.4 spread and thick smoke.
  R.push(
    pairRule('OIL', 'FIRE', {
      chance: 0.95,
      flagA: Flag.BURNING,
      heat: 60,
      emit: { mat: 'SMOKE', count: 2 },
      event: 'oil_fire',
    }),
    pairRule('MACHINE_OIL', 'FIRE', {
      chance: 0.95,
      flagA: Flag.BURNING,
      heat: 60,
      emit: { mat: 'SMOKE', count: 2 },
      event: 'oil_fire',
    }),
  );

  // ALCOHOL + FIRE — near-invisible flame, very fast spread, no smoke.
  R.push(
    pairRule('ALCOHOL', 'FIRE', {
      chance: 1,
      flagA: Flag.BURNING,
      heat: 90,
      event: 'alcohol_fire',
    }),
  );

  // METHANE + spark — volumetric detonation. Scale comes for free: each
  // detonating cell heats the next one past its ignition point.
  R.push(
    pairRule('METHANE', 'FIRE', {
      chance: 1,
      outA: 'consume',
      heat: 500,
      emit: { mat: 'FIRE', count: 4, velocity: 2 },
      event: 'methane_detonation',
    }),
    pairRule('METHANE', 'LIGHTNING', {
      chance: 1,
      outA: 'consume',
      heat: 500,
      emit: { mat: 'FIRE', count: 4, velocity: 2 },
      event: 'methane_detonation',
    }),
  );

  // CHLORINE + WATER — sinks and becomes a toxic liquid film.
  R.push(
    pairRule('CHLORINE', 'WATER', {
      chance: 0.4,
      outA: 'consume',
      outB: 'ACID',
      event: 'chlorine_water_film',
    }),
  );

  // MERCURY + CHARGED — charge.ts does the superconducting; this records it.
  R.push(
    matRule('MERCURY', Tag.CONDUCTIVE, {
      chance: 0.4,
      requiresFlagB: Flag.CHARGED,
      charge: 1,
      event: 'mercury_superconducts',
    }),
  );

  // REACTOR_COOLANT + PLASMA — flash-boil, vapour, and the pressure spike that
  // pressure.ts turns into a rupture.
  R.push(
    pairRule('REACTOR_COOLANT', 'PLASMA', {
      chance: 1,
      outA: 'FLASH_VAPOR',
      outB: 'consume',
      heat: 220,
      emit: { mat: 'FLASH_VAPOR', count: 4, velocity: 2 },
      event: 'coolant_flash_boil',
    }),
    // ...and the same thing, less violently, for any other open heat. Coolant
    // is a Coldforge liquid and plasma is three links up a chain the player may
    // never build; a torch held to the pipe has to do something, or the biome's
    // signature hazard is a hazard nobody ever triggers.
    matRule('REACTOR_COOLANT', Tag.PYRO, {
      chance: 0.7,
      minTemp: 120,
      outA: 'FLASH_VAPOR',
      heat: 90,
      emit: { mat: 'FLASH_VAPOR', count: 2, velocity: 2 },
      event: 'coolant_flash_boil',
      priority: 6,
    }),
  );

  // MACHINE_OIL + an overheating machine — it ignites *inside* the machine.
  R.push(
    matRule('MACHINE_OIL', Tag.MACHINE, {
      chance: 0.7,
      minTemp: 300,
      flagA: Flag.BURNING,
      heat: 150,
      emit: { mat: 'SMOKE', count: 1 },
      event: 'machine_oil_ignites',
      priority: 6,
    }),
  );

  // LIQUID_VOID + RADIANT_ICHOR — annihilation flash. The 12-cell erase is an
  // ability-scale effect; the contact itself just stops both materials existing.
  R.push(
    pairRule('LIQUID_VOID', 'RADIANT_ICHOR', {
      chance: 1,
      outA: 'consume',
      outB: 'consume',
      heat: 200,
      emit: { mat: 'FLASH_VAPOR', count: 3, velocity: 2 },
      event: 'annihilation_flash',
    }),
  );

  // NANITE_SWARM + corpse — the corpse animates. Spawning the construct is the
  // enemy system's hook off this event; the cells become swarm either way.
  R.push(
    pairRule('NANITE_SWARM', 'ROTTEN_FLESH', {
      chance: 0.25,
      outB: 'NANITE_SWARM',
      event: 'nanite_reanimation',
    }),
    pairRule('NANITE_SWARM', 'BONE', {
      chance: 0.15,
      outB: 'NANITE_SWARM',
      event: 'nanite_reanimation',
    }),
  );

  // HOLY_FIRE + ROTTEN_FLESH — burns without fuel until the flesh is gone.
  R.push(
    pairRule('HOLY_FIRE', 'ROTTEN_FLESH', {
      chance: 0.9,
      heat: 320,
      flagB: Flag.BURNING,
      event: 'holy_fire_consumes_rot',
    }),
  );

  // SPORE_CLOUD + FIRE — the fuel-air explosion.
  R.push(
    pairRule('SPORE_CLOUD', 'FIRE', {
      chance: 1,
      outA: 'consume',
      heat: 450,
      emit: { mat: 'FIRE', count: 5, velocity: 2 },
      event: 'fuel_air_explosion',
    }),
    pairRule('SPORE_DUST', 'FIRE', {
      chance: 1,
      outA: 'consume',
      heat: 400,
      emit: { mat: 'FIRE', count: 4, velocity: 2 },
      event: 'fuel_air_explosion',
    }),
    // Coal dust in air behaves the same way, and for the same reason.
    pairRule('COAL_DUST', 'FIRE', {
      chance: 1,
      outA: 'consume',
      heat: 420,
      emit: { mat: 'FIRE', count: 4, velocity: 2 },
      event: 'dust_explosion',
    }),
  );

  // ICE + SALT — melts to brine, which is conductive and corrosive.
  R.push(
    pairRule('ICE', 'SALT', {
      chance: 0.35,
      outA: 'BRINE',
      outB: 'consume',
      event: 'ice_salt_brine',
    }),
    pairRule('SNOW', 'SALT', {
      chance: 0.35,
      outA: 'BRINE',
      outB: 'consume',
      event: 'ice_salt_brine',
    }),
  );

  // PLAGUE_SLURRY + SANCTIFIED_MIST — neutralised, leaving plain salt.
  R.push(
    pairRule('PLAGUE_SLURRY', 'SANCTIFIED_MIST', {
      chance: 0.6,
      outA: 'SALT',
      outB: 'consume',
      event: 'plague_neutralised',
    }),
  );

  // TAR + FIRE — slow, extremely long burn. burnDuration does the work.
  R.push(
    pairRule('TAR', 'FIRE', {
      chance: 0.5,
      flagA: Flag.BURNING,
      heat: 140,
      emit: { mat: 'SMOKE', count: 1 },
      event: 'tar_fire',
    }),
    pairRule('CRUDE_TAR', 'FIRE', {
      chance: 0.6,
      flagA: Flag.BURNING,
      heat: 140,
      emit: { mat: 'SMOKE', count: 1 },
      event: 'tar_fire',
    }),
  );

  // CRYSTAL + LIGHTNING — stores the charge and releases it as a beam.
  // "releases as a beam on the next tick" — the beam is a plasma cell. Emitting
  // it is what makes PLASMA a material the world can produce rather than a
  // roster entry with a definition and no path to existing.
  R.push(
    pairRule('CRYSTAL', 'LIGHTNING', {
      chance: 1,
      outB: 'consume',
      flagA: Flag.LIT,
      charge: 1,
      emit: { mat: 'PLASMA', count: 1, velocity: 2 },
      event: 'crystal_stores_charge',
    }),
    pairRule('RUNESTONE', 'LIGHTNING', {
      chance: 1,
      outB: 'consume',
      flagA: Flag.LIT,
      charge: 1,
      emit: { mat: 'PLASMA', count: 1, velocity: 2 },
      event: 'crystal_stores_charge',
    }),
  );

  // VOIDGLASS + light — it absorbs it. Radiant matter that touches voidglass
  // simply stops, which is also how the light field is told the zone is dark.
  R.push(
    matRule('VOIDGLASS', Tag.RADIANT, {
      chance: 0.8,
      outB: 'consume',
      event: 'voidglass_absorbs_light',
      priority: 6,
    }),
  );

  // GRAVE_SOIL + BLOOD — something stands up. The revenant is spawned by the
  // enemy system off this event; the cells leave corpse stone behind.
  R.push(
    pairRule('GRAVE_SOIL', 'BLOOD', {
      chance: 0.2,
      outA: 'CORPSE_STONE',
      outB: 'consume',
      emit: { mat: 'CURSED_BLOOD', count: 1 },
      event: 'revenant_rises',
    }),
    pairRule('GRAVE_SOIL', 'CURSED_BLOOD', {
      chance: 0.3,
      outA: 'CORPSE_STONE',
      outB: 'consume',
      event: 'revenant_rises',
    }),
  );

  // LIVING_RESIN + heat — hardens into petrified wood. Buildable terrain, and
  // it outranks the ignition row so resin hardens rather than burns.
  R.push(
    matRule('LIVING_RESIN', Tag.PYRO, {
      chance: 0.5,
      minTemp: 200,
      outA: 'PETRIFIED_WOOD',
      event: 'resin_hardens',
      priority: 6,
    }),
  );

  // SNOW + BURNING — melts to water, which puts the fire out, which then
  // conducts. The chain the material doc opens with.
  R.push(
    matRule('SNOW', COMBUSTIBLE, {
      chance: 0.8,
      requiresFlagB: Flag.BURNING,
      outA: 'WATER',
      flagB: Flag.WET,
      heat: -30,
      event: 'snow_quenches_fire',
      priority: 6,
    }),
    matRule('SNOW', Tag.PYRO, {
      chance: 0.7,
      outA: 'WATER',
      heat: -30,
      event: 'snow_melts',
    }),
    matRule('ICE', Tag.PYRO, {
      chance: 0.4,
      outA: 'WATER',
      heat: -30,
      event: 'ice_melts',
    }),
  );

  return R;
}
